import { SemanticTokenColor } from '@morressier/alembic/_dist/cjs/types';
import { DecisionMenuActions } from 'src/components/Submission/decisionModals/decisionModalHelper';
import { IDependentValue } from '@publishing-engine/domain/models';
export const actionToLabelAndDescription: Record<
  DecisionMenuActions,
  { label: string; description: string; headingColor?: SemanticTokenColor }
> = {
  'EditorialSubmissionDecision#approveForPeerReview': {
    label: 'Approve for Peer Review',
    description:
      'By approving for peer review, you will send this Submission into the defined Peer Review process.'
  },
  EditorialRejectSubmission: {
    label: 'Reject',
    description:
      'By rejecting, you will be prompted to select a reason for this decision before confirming.'
  },
  EditorialRequestImmediateRevision: {
    label: 'Immediate Revision',
    description:
      'When inviting the Author to revise, you will need to provide a comment outlining the requested changes before confirming.'
  },
  'SubmitRevision#form': {
    label: 'Revise Submission',
    description:
      'By submitting the Revision, you will send the updated submission to the next phase.'
  },
  'SubmitRevision#fields': {
    label: 'Respond to Comments',
    description:
      'You must respond to the Reviewer comments before submitting your revision.'
  },
  RequestSubmissionDeadlineExtension: {
    label: 'Request Extension',
    description: ''
  },
  RequestRevision: {
    label: 'Invite to Revise',
    description:
      "When you invite the Author to Revise, they'll receive the selected Reviewer feedback, attachments, and your prepared Author response. You can edit your response before sending the invite."
  },
  EditorialWithdrawSubmission: {
    label: 'Withdraw',
    description:
      'By withdrawing, you will remove this Submission from the publication process.'
  },
  EditorialAcceptSubmissionWithdrawal: {
    label: 'Withdraw',
    description:
      'By withdrawing, you will remove this Submission from the publication process.'
  },
  AuthorRequestSubmissionWithdrawal: {
    label: 'Request Withdrawal',
    description:
      'By withdrawing, you will request the Editors to remove this Submission from the publication process. This request can be cancelled at any time.'
  },
  AuthorRevokeSubmissionWithdrawalRequest: {
    label: 'Revert Withdrawal Request',
    description:
      'By reverting your withdrawal request, you will restore this Submission into the publication process and notify the Editors of this decision.'
  },
  ExtendSubmissionDeadline: {
    label: 'Extend Author Deadline',
    description: ''
  },
  EditorialOfferTransfer: {
    label: 'Reject and Transfer',
    description:
      'By rejecting and transferring, you will be prompted to select a reason for this decision, and invite the Author to continue their publication process with another Journal.'
  },
  DeclineTransfer: {
    label: 'Decline Transfer',
    description: 'If you decline this offer, this Submission will be rejected.'
  },
  'AcceptTransferOffer#form': {
    label: 'Accept Transfer',
    description:
      'If you accept the offer, you can choose one Journal to proceed with the publication process.'
  },
  ApproveSubmissionForPreProduction: {
    label: 'Proceed to Provisional Acceptance',
    description:
      'By proceeding, the submission will move to Pre-Production, where you can complete checks and request any Author updates before publication.'
  },
  AcceptForFormalAcceptance: {
    label: 'Proceed to Formal Acceptance',
    description:
      'By proceeding, the Submission will forwarded to your Production system. Ensure all communications with the Author are resolved and no further updates are required before proceeding.'
  },
  'EditorialSubmitChangeRequest#form': {
    label: 'Request Submission Updates',
    description:
      'Notify the author of all logged change requests for this submission.'
  },
  'AuthorCompleteChangeRequest#form': {
    label: 'Submit Changes',
    description: ''
  },
  EditorialUnsubmitSubmission: {
    label: 'Unsubmit',
    description:
      "By unsubmitting, you will revert this Submission to a draft, allowing the Author to edit it. The author's data will be preserved."
  },
  AuthorResubmitSubmission: {
    label: 'Resubmit Submission',
    description:
      'By resubmitting this Submission, you will send the updated submission to be assessed again by Editors.'
  },
  RevertDecision: {
    label: 'Revert Decision',
    description:
      'Return to the previous phase of the workflow for this submission. This is an irreversible action.',
    headingColor: 'actionPrimaryCautionFill'
  },
  RequestEarlyAssessment: {
    label: 'Get Expert Support',
    description:
      'Invite a board member to assess the Submission and provide recommendations.'
  },
  RequestAdjudication: {
    label: 'Invite Adjudicators',
    description:
      'Invite a referee to assess the Submission’s reviews and provide recommendations.'
  }
};

export function getActionLabel(actionName?: string) {
  if (
    actionName &&
    actionToLabelAndDescription[actionName as DecisionMenuActions]?.label
  ) {
    return actionToLabelAndDescription[actionName as DecisionMenuActions].label;
  }
  return actionName;
}

export function getActionDescription(actionName: string) {
  if (
    actionToLabelAndDescription[actionName as DecisionMenuActions]?.description
  ) {
    return actionToLabelAndDescription[actionName as DecisionMenuActions]
      .description;
  }
  return null;
}

export function getActionLabelAndDescription(actionName: string) {
  const data = actionToLabelAndDescription[actionName as DecisionMenuActions];
  if (data) {
    return data;
  }

  return null;
}

export const isDependentValue = (val: any): val is IDependentValue<string> =>
  typeof val === 'object' && val !== null && 'exists' in val && 'value' in val;
